import { Box, InputLabel } from "@mui/material";
import { useEffect, useState } from "react";
import { useDataProvider } from "react-admin";
import { ResourceName, type Client, type Policy, type Vendor } from "@/types";
import { MultiSelectFilter } from "../shared/components/MultiSelectFilter";
import { DateRangeFilter } from "../shared/components/DateRangeFilter";

interface StoplossClaimsDashboardFiltersProps {
  allPolicies: Policy[];
  filters: Record<string, string[]>;
}

/**
 * Inline filter component for the Stoploss Claims Dashboard.
 * Displays carrier and policyholder filters in a compact horizontal layout.
 *
 * Options are derived from the (lightweight, capped) policy list alone — the
 * revamp (Feature #6161) dropped the old 10,000-row stoploss-claims fetch, so
 * the option universe is every carrier/client that appears on a policy rather
 * than only those with existing claims.
 */
export const StoplossClaimsDashboardFilters = ({ allPolicies, filters }: StoplossClaimsDashboardFiltersProps) => {
  const [carriers, setCarriers] = useState<{ id: string; name: string }[]>([]);
  const [policyholders, setPolicyholders] = useState<{ id: string; name: string }[]>([]);
  const dataProvider = useDataProvider();

  useEffect(() => {
    // Unique carrier (vendor) and client IDs across the policy list.
    const insuranceProviderIds = new Set<string>();
    const clientIds = new Set<string>();

    allPolicies.forEach((policy) => {
      if (policy.vendor_id_ref) insuranceProviderIds.add(policy.vendor_id_ref);
      if (policy.client_id_ref) clientIds.add(policy.client_id_ref);
    });

    // Fetch vendors (carriers)
    if (insuranceProviderIds.size > 0) {
      try {
        void dataProvider
          .getList<Vendor>(ResourceName.VENDORS, {
            pagination: { page: 1, perPage: 1000 },
            sort: { field: "company_name", order: "ASC" },
            filter: {},
          })
          .then(({ data }) => {
            const carrierChoices = data
              .filter((vendor) => insuranceProviderIds.has(vendor.id))
              .map((vendor) => ({
                id: vendor.id,
                name: vendor.company_name || "Unknown Carrier",
              }))
              .sort((a, b) => a.name.localeCompare(b.name));
            setCarriers(carrierChoices);
          });
      } catch (error) {
        console.error("Error fetching vendors:", error);
        setCarriers([]);
      }
    }

    // Fetch clients (policyholders) - filter based on selected carriers
    if (clientIds.size > 0) {
      dataProvider
        .getList<Client>(ResourceName.CLIENTS, {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: "client_legal_name", order: "ASC" },
          filter: {},
        })
        .then(({ data }) => {
          const policyholderChoices = data
            .filter((client) => clientIds.has(String(client.id)))
            .map((client) => ({
              id: String(client.id),
              name: client.client_legal_name || "Unknown Client",
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
          setPolicyholders(policyholderChoices);
        })
        .catch((error) => {
          console.error("Error fetching clients:", error);
          setPolicyholders([]);
        });
    }
  }, [allPolicies, dataProvider, filters.client_id_ref]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <InputLabel>Filters</InputLabel>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ minWidth: 250, flexGrow: 1, maxWidth: 400 }}>
          <MultiSelectFilter source="vendor_id_ref" label="Carrier" choices={carriers} />
        </Box>
        <Box sx={{ minWidth: 250, flexGrow: 1, maxWidth: 400 }}>
          <MultiSelectFilter source="client_id_ref" label="Client (Policyholder)" choices={policyholders} />
        </Box>
        <DateRangeFilter />
      </Box>
    </Box>
  );
};
